import React, { useEffect, useState } from "react";
import {
  AvField,
  AvForm,
} from "availity-reactstrap-validation";
import { useTranslation } from "react-i18next";
import { NextButton } from "./onboardingManager"; // adjust if path is different
import { OnboardingPageProps } from "./onboardingManager"; // same here
import { CustomCheckboxGroup } from "../../components/CustomCheckboxGroup";

export const BusinessUseDescription = ({
  onNext,
  validate,
  handleFieldChange,
  formRef,
  setCurrentPage,
}: OnboardingPageProps): JSX.Element => {
  const { t } = useTranslation();
  const handleValidSubmit = (e, values) => {
    return onNext({ e, clientValues: values });
  };
  useEffect(() => {
    setCurrentPage?.((prev) => ({ ...prev!, state: "business_use" }));
  }, [setCurrentPage]);

  return (
    <AvForm onValidSubmit={handleValidSubmit} ref={formRef}>
      <h3>What does your business do?</h3>
      <AvField
        name="businessUseDescription"
        autoFocus
        label="Please describe your business, who you serve, and what services or products you offer."
        type="textarea"
        style={{ minHeight: "150px" }}
        placeholder="Minimum 30 characters"
        validate={{
          required: { value: true, errorMessage: t("required_field") },
          minLength: {
            value: 30,
            errorMessage: t("minimum_length", { length: 30 }),
          },
          async: validate,
        }}
        onChange={handleFieldChange}
      />
      <NextButton />
    </AvForm>
  );
};

export const BusinessUseCustomerTypeComponent = (
  props: OnboardingPageProps,
): JSX.Element => {
  return (
    <BusinessUseCheckboxQuestion
      key="businessUseCustomersType"
      {...props}
      label="Who will you be debiting using Rotessa?"
    fieldName="businessUseCustomersType"
      options={[
        "Individual customers",
        "Other businesses",
        "Tenants or leaseholders",
        "Donors",
        "Members or subscribers",
        "Patients or clients",
        "Other",
      ]}
    />
  );
};

export const BusinessUseKindOfPaymentsComponent = (
  props: OnboardingPageProps,
): JSX.Element => {
  return (
    <BusinessUseCheckboxQuestion
      key="businessUseKindOfPayments"
      {...props}
      label="What kind of payments are you collecting through Rotessa?"
      fieldName="businessUseKindOfPayments"
      options={[
        "Recurring service fees",
        "Invoices for completed work",
        "Rent or lease payments",
        "Loan repayments",
        "Membership or subscription fees",
        "Donations",
        "Product sales",
        "Tuition or childcare payments",
        "Other",
      ]}
    />
  );
};

export const BusinessUseCustomersAgreementTypeComponent = (
  props: OnboardingPageProps,
): JSX.Element => {
  return (
    <BusinessUseCheckboxQuestion
      key="businessUseCustomersAgreementType"
      {...props}
      label="How do your customers agree to these payments?"
      fieldName="businessUseCustomersAgreementType"
      options={[
        "Signed agreement or contract",
        "Terms agreed to at checkout or signup",
        "Invoice or billing notice sent before payment",
        "Verbal agreement",
        "Other",
      ]}
    />
  );
};

export const BusinessUsePaymentTimingComponent = (
  props: OnboardingPageProps,
): JSX.Element => {
  return (
    <BusinessUseCheckboxQuestion
      key="businessUsePaymentTiming"
      {...props}
      label="When do you typically collect payment?"
      fieldName="businessUsePaymentTiming"
      options={[
        "Before providing the service",
        "After the service is completed",
        "On a set recurring schedule (e.g., 1st of every month)",
        "Other",
      ]}
    />
  );
};

interface CheckboxQuestionProps extends OnboardingPageProps {
  label?: string;
  fieldName: string;
  options: string[];
}

export const BusinessUseCheckboxQuestion = ({
  label,
  fieldName,
  options,
  onNext,
  setCurrentPage,
  handleFieldChange,
  validate,
  formRef,
}: CheckboxQuestionProps): JSX.Element => {
  const { t } = useTranslation();

  const [selected, setSelected] = useState<string[]>([]);
  const [otherText, setOtherText] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPage?.((prev) => ({ ...prev!, state: "business_use" }));
  }, [setCurrentPage]);

  const handleValidSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (selected.length === 0) {
      setError(t("required_checkbox_group"));
      return;
    }
    if (selected.includes("Other") && otherText.trim() === "") {
      setError(t("required_field"));
      return;
    }
    setError(null);

    const ordered = selected.includes("Other")
      ? selected.filter((opt) => opt !== "Other").concat("Other")
      : selected;

    const answers = ordered.map((v) =>
      v === "Other" ? `Other: ${otherText}` : v,
    );

    const answersAsString = answers.join(", ");
    return onNext({ e, clientValues: { [fieldName]: answersAsString } });
  };

  return (
    <AvForm onValidSubmit={handleValidSubmit} ref={formRef}>
      <CustomCheckboxGroup
        fieldName={fieldName}
        label={label}
        helperText={"Select all that apply" }
        options={options}
        selected={selected}
        setSelected={setSelected}
        otherText={otherText}
        setOtherText={setOtherText}
        error={error}
        setError={setError}
        validate={validate as (value: string) => Promise<boolean>}
        handleFieldChange={handleFieldChange as (e: any, value: string) => void}
      />
      <NextButton />
    </AvForm>
  );
};
